import { Button } from '@/components/common/button'
import { Input } from '@/components/common/input'
import { Popover, PopoverContent, PopoverList, PopoverListItem, PopoverTrigger } from '@/components/common/popover'
import { ScrollArea } from '@/components/common/scrollArea'
import { ArrowIcon } from '@/components/icons/arrow'
import type { PositionsList } from '@/queries/positions.query'
import type { VaultApy, VaultApyRange } from '@/types/apy'
import type { Vault } from '@/types/vault'
import { getApyCopy, getApyValue } from '@/utils/apy'
import { isMobileUserAgent } from '@/utils/browser/isMobileUserAgent'
import { cn } from '@/utils/cn'
import { getCuratorDisplayName } from '@/utils/curator'
import { getNetworkName } from '@/utils/network/getNetworkName'
import { getProtocolDisplayName } from '@/utils/protocol'
import { getValueColor } from '@/utils/styles/getValueColor'
import type { ChangeEvent } from 'react'
import { useCallback, useMemo, useState } from 'react'
import { Checkbox } from '../common/checkbox'
import { Label } from '../common/label'
import { NetworkImage } from '../common/networkImage'
import { ProtocolImage } from '../common/protocolImage'
import { TokenImage } from '../common/tokenImage'

function ApyBadge({
  apy,
  apyRange,
  includeRewards,
}: { apy: VaultApy; apyRange: VaultApyRange; includeRewards: boolean }) {
  return (
    <span className={cn('shrink-0 text-sm', getValueColor(getApyValue({ apy, apyRange, includeRewards })))}>
      {getApyCopy({ apy, apyRange, includeRewards })}
    </span>
  )
}

export type BenchmarkItem = {
  type: 'benchmark'
  id: string
  name: string
  symbol: string
}

interface VaultSelectorProps {
  vaults: Vault[]
  selectedVaults: Vault[]
  setSelectedVaults: (vaults: Vault[]) => void
  benchmarks?: BenchmarkItem[]
  selectedBenchmarks?: BenchmarkItem[]
  setSelectedBenchmarks?: (benchmarks: BenchmarkItem[]) => void
  positionsList?: PositionsList
  isLoading?: boolean
  apyRange?: VaultApyRange
  includeRewards?: boolean
  benchmarkApyMap?: Partial<Record<string, VaultApy>>
  className?: string
}

const getNetworkSearchTerms = (vault: Vault) => {
  const networkName = vault.network.name.toLowerCase()
  const displayName = getNetworkName(networkName)
  return `${networkName} ${displayName.toLowerCase()}`
}

function searchVaults(vaults: Vault[], searchTerm: string): Vault[] {
  if (!searchTerm) return vaults

  const lowerSearch = searchTerm.toLowerCase()

  return vaults.filter((vault) => {
    const searchableText = [
      vault.name,
      vault.protocol.name,
      getProtocolDisplayName(vault.protocol),
      vault.asset.name,
      vault.asset.symbol,
      vault.curator?.name || '',
      vault.curator ? getCuratorDisplayName(vault.curator) : '',
      getNetworkSearchTerms(vault),
    ]
      .join(' ')
      .toLowerCase()

    return searchableText.includes(lowerSearch)
  })
}

const getVaultKey = (vault: { vaultId: string; network: { name: string } }) => `${vault.network.name}:${vault.vaultId}`

export function VaultSelector({
  vaults,
  selectedVaults,
  setSelectedVaults,
  benchmarks = [],
  selectedBenchmarks = [],
  setSelectedBenchmarks,
  positionsList,
  isLoading = false,
  apyRange = '30day',
  includeRewards = false,
  benchmarkApyMap,
  className,
}: VaultSelectorProps) {
  const [open, setOpen] = useState(false)
  const [search, setSearch] = useState('')
  const [selectedVaultsSnapshot, setSelectedVaultsSnapshot] = useState<Vault[]>([])
  const [selectedBenchmarksSnapshot, setSelectedBenchmarksSnapshot] = useState<BenchmarkItem[]>([])

  const onSearchChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    setSearch(event.target.value)
  }, [])

  const handleOpenChange = useCallback(
    (open: boolean) => {
      setOpen(open)
      if (open) {
        setSelectedVaultsSnapshot([...selectedVaults])
        setSelectedBenchmarksSnapshot([...selectedBenchmarks])
      }
    },
    [selectedVaults, selectedBenchmarks]
  )

  const { filteredBenchmarks, selectedVaultsFiltered, userVaults, otherVaults } = useMemo(() => {
    const searched = searchVaults(vaults, search)
    const positionKeys = new Set(positionsList?.map(getVaultKey) ?? [])
    const snapshotKeys = new Set(selectedVaultsSnapshot.map(getVaultKey))
    const benchmarkSnapshotKeys = new Set(selectedBenchmarksSnapshot.map((b) => b.id))

    const lowerSearch = search.toLowerCase()
    const filteredBenchmarks = benchmarks.filter((benchmark) => {
      if (!search) return true
      return benchmark.name.toLowerCase().includes(lowerSearch) || benchmark.symbol.toLowerCase().includes(lowerSearch)
    })

    const selectedVaultsFiltered = searched.filter((vault) => {
      const vaultKey = getVaultKey(vault)
      return snapshotKeys.has(vaultKey)
    })

    const userVaults = searched.filter((vault) => {
      const vaultKey = getVaultKey(vault)
      return positionKeys.has(vaultKey) && !snapshotKeys.has(vaultKey)
    })

    const otherVaults = searched.filter((vault) => {
      const vaultKey = getVaultKey(vault)
      return !positionKeys.has(vaultKey) && !snapshotKeys.has(vaultKey)
    })

    const sortByTvl = (a: Vault, b: Vault) => {
      const aTvl = a.tvl?.usd ? Number.parseFloat(a.tvl.usd) : 0
      const bTvl = b.tvl?.usd ? Number.parseFloat(b.tvl.usd) : 0
      return bTvl - aTvl
    }

    return {
      filteredBenchmarks: filteredBenchmarks.sort((a, b) => {
        const aSelected = benchmarkSnapshotKeys.has(a.id)
        const bSelected = benchmarkSnapshotKeys.has(b.id)
        if (aSelected && !bSelected) return -1
        if (!aSelected && bSelected) return 1
        return 0
      }),
      selectedVaultsFiltered: selectedVaultsFiltered.sort(sortByTvl),
      userVaults: userVaults.sort(sortByTvl),
      otherVaults: otherVaults.sort(sortByTvl),
    }
  }, [vaults, search, positionsList, selectedVaultsSnapshot, benchmarks, selectedBenchmarksSnapshot])

  const handleVaultToggle = useCallback(
    (vault: Vault) => {
      const vaultKey = getVaultKey(vault)
      const isSelected = selectedVaults.some((v) => getVaultKey(v) === vaultKey)

      if (isSelected) {
        setSelectedVaults(selectedVaults.filter((v) => getVaultKey(v) !== vaultKey))
      } else {
        setSelectedVaults([...selectedVaults, vault])
      }
    },
    [selectedVaults, setSelectedVaults]
  )

  const handleBenchmarkToggle = useCallback(
    (benchmark: BenchmarkItem) => {
      if (!setSelectedBenchmarks) return
      const isSelected = selectedBenchmarks.some((b) => b.id === benchmark.id)

      if (isSelected) {
        setSelectedBenchmarks(selectedBenchmarks.filter((b) => b.id !== benchmark.id))
      } else {
        setSelectedBenchmarks([...selectedBenchmarks, benchmark])
      }
    },
    [selectedBenchmarks, setSelectedBenchmarks]
  )

  const handleAutoFocus = useCallback((e: Event) => {
    if (isMobileUserAgent()) {
      e.preventDefault()
    }
  }, [])

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <Button variant="input" className={cn('group flex h-8 gap-2 px-3 text-left', className)}>
          <div className="flex w-full max-w-full items-center gap-2">
            <span className="text-lg leading-none">+</span>
            <span>Add vault or benchmark</span>
          </div>
          <div className="group-data-[state=open]:-scale-y-100 transition-transform">
            <ArrowIcon direction="down" size={20} />
          </div>
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" onOpenAutoFocus={handleAutoFocus} className="w-[400px] max-w-[90vw]">
        <div className="flex hocus-within:bg-input-hover transition-colors">
          <Input
            placeholder="Find vault by name, protocol, token, or curator"
            onChange={onSearchChange}
            value={search}
            inputMode="text"
            className="border-none bg-transparent ring-inset"
          />
        </div>
        {isLoading ? (
          <div className="flex h-96 items-center justify-center border-t">
            <span className="text-body-foreground text-sm">Loading vaults...</span>
          </div>
        ) : (
          (filteredBenchmarks.length > 0 ||
            selectedVaultsFiltered.length > 0 ||
            userVaults.length > 0 ||
            otherVaults.length > 0) && (
            <ScrollArea type="auto" className="h-96 border-t">
              <PopoverList>
                {filteredBenchmarks.length > 0 && (
                  <>
                    <li className="bg-surface px-3 py-2 font-semibold text-body-foreground text-xs uppercase">
                      Benchmarks
                    </li>
                    {filteredBenchmarks.map((benchmark) => {
                      const isSelected = selectedBenchmarks.some((b) => b.id === benchmark.id)

                      return (
                        <PopoverListItem key={benchmark.id} className="max-w-full truncate border-t first:border-none">
                          <Label className={cn('w-full max-w-full gap-3 px-3 py-2.5')}>
                            <div className="flex min-w-0 flex-1 items-center gap-2 truncate">
                              <TokenImage symbol={benchmark.symbol} className="size-7" />
                              <span className="max-w-full truncate text-sm">{benchmark.name}</span>
                            </div>
                            {benchmarkApyMap?.[benchmark.id] && (
                              <ApyBadge
                                apy={benchmarkApyMap[benchmark.id]!}
                                apyRange={apyRange}
                                includeRewards={includeRewards}
                              />
                            )}
                            <Checkbox onCheckedChange={() => handleBenchmarkToggle(benchmark)} checked={isSelected} />
                          </Label>
                        </PopoverListItem>
                      )
                    })}
                  </>
                )}
                {selectedVaultsFiltered.length > 0 && (
                  <>
                    <li className="border-t bg-surface px-3 py-2 font-semibold text-body-foreground text-xs uppercase">
                      Selected vaults
                    </li>
                    {selectedVaultsFiltered.map((vault) => {
                      const vaultKey = getVaultKey(vault)
                      const isSelected = selectedVaults.some((v) => getVaultKey(v) === vaultKey)

                      return (
                        <PopoverListItem key={vaultKey} className="max-w-full truncate border-t first:border-none">
                          <Label className={cn('w-full max-w-full gap-3 px-3 py-2.5')}>
                            <div className="flex min-w-0 flex-1 items-center gap-2 truncate">
                              <div className="relative">
                                <TokenImage symbol={vault.asset.symbol} className="size-7" />
                                <NetworkImage
                                  network={vault.network.name}
                                  className="absolute right-0 bottom-0 size-3.5"
                                />
                              </div>
                              <ProtocolImage protocol={vault.protocol} className="size-7 shrink-0" />
                              <span className="max-w-full truncate text-sm first-letter:uppercase">{vault.name}</span>
                            </div>
                            {vault.apy && (
                              <ApyBadge apy={vault.apy} apyRange={apyRange} includeRewards={includeRewards} />
                            )}
                            <Checkbox onCheckedChange={() => handleVaultToggle(vault)} checked={isSelected} />
                          </Label>
                        </PopoverListItem>
                      )
                    })}
                  </>
                )}
                {userVaults.length > 0 && (
                  <>
                    <li className="border-t bg-surface px-3 py-2 font-semibold text-body-foreground text-xs uppercase">
                      Your Vaults
                    </li>
                    {userVaults.map((vault) => {
                      const vaultKey = getVaultKey(vault)
                      const isSelected = selectedVaults.some((v) => getVaultKey(v) === vaultKey)

                      return (
                        <PopoverListItem key={vaultKey} className="max-w-full truncate border-t first:border-none">
                          <Label className={cn('w-full max-w-full gap-3 px-3 py-2.5')}>
                            <div className="flex min-w-0 flex-1 items-center gap-2 truncate">
                              <div className="relative">
                                <TokenImage symbol={vault.asset.symbol} className="size-7" />
                                <NetworkImage
                                  network={vault.network.name}
                                  className="absolute right-0 bottom-0 size-3.5"
                                />
                              </div>
                              <ProtocolImage protocol={vault.protocol} className="size-7 shrink-0" />
                              <span className="max-w-full truncate text-sm first-letter:uppercase">{vault.name}</span>
                            </div>
                            {vault.apy && (
                              <ApyBadge apy={vault.apy} apyRange={apyRange} includeRewards={includeRewards} />
                            )}
                            <Checkbox onCheckedChange={() => handleVaultToggle(vault)} checked={isSelected} />
                          </Label>
                        </PopoverListItem>
                      )
                    })}
                  </>
                )}
                {otherVaults.length > 0 && (
                  <>
                    <li className="border-t bg-surface px-3 py-2 font-semibold text-body-foreground text-xs uppercase">
                      Rest Vaults
                    </li>
                    {otherVaults.map((vault) => {
                      const vaultKey = getVaultKey(vault)
                      const isSelected = selectedVaults.some((v) => getVaultKey(v) === vaultKey)

                      return (
                        <PopoverListItem key={vaultKey} className="max-w-full truncate border-t first:border-none">
                          <Label className={cn('w-full max-w-full gap-3 px-3 py-2.5')}>
                            <div className="flex min-w-0 flex-1 items-center gap-2 truncate">
                              <div className="relative">
                                <TokenImage symbol={vault.asset.symbol} className="size-7" />
                                <NetworkImage
                                  network={vault.network.name}
                                  className="absolute right-0 bottom-0 size-3.5"
                                />
                              </div>
                              <ProtocolImage protocol={vault.protocol} className="size-7 shrink-0" />
                              <span className="max-w-full truncate text-sm first-letter:uppercase">{vault.name}</span>
                            </div>
                            {vault.apy && (
                              <ApyBadge apy={vault.apy} apyRange={apyRange} includeRewards={includeRewards} />
                            )}
                            <Checkbox onCheckedChange={() => handleVaultToggle(vault)} checked={isSelected} />
                          </Label>
                        </PopoverListItem>
                      )
                    })}
                  </>
                )}
              </PopoverList>
            </ScrollArea>
          )
        )}
      </PopoverContent>
    </Popover>
  )
}
